import { useRuntimeConfig } from '#imports'
import { useSecretManager } from '~/composables/useSecretManager'
import { useUserStore } from '~/stores/user'
import { isLocalPreviewRuntime } from '~/utils/analyticsEnv.js'
import { detectAutomation } from '~/utils/automation'

export const FOOTER_MARK_PATH = '/nav-logo.webp'
export const SIGNING_BOOTSTRAP_PATH = '/api/aigc/config'
export const AUTOMATION_REPORT_PATH = '/api/aigc/track/log'
export const LIP_SYNC_CREATE_TASK_PATH = '/api/aigc/comprehensive/lip-sync/create-task'
export const AI_AVATAR_CREATE_TASK_PATH = '/api/aigc/video/v1/video-generation/create-task'
export const TALKING_PHOTO_CREATE_TASK_PATH = '/api/aigc/comprehensive/lip-sync/create-talking-task'
export const AVATAR_DIALOGUE_CREATE_TASK_PATH = '/api/aigc/comprehensive/lip-sync/create-talk-multi-task'

const SIGNING_PATH_PREFIXES = ['/api/aigc/']
const UUID_STORAGE_KEY = 'ls_id_uuid'
const UUID_COOKIE_NAME = 'uuid'
export const WAPU_ID_HEADER = 'wapuId'
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

let initPromise: Promise<void> | null = null
let bootstrapPromise: Promise<unknown> | null = null
let loginPromptSuppressCount = 0

/** Background init (signing bootstrap, stale session refresh) must not open the login modal. */
export function isLoginPromptSuppressed() {
  return loginPromptSuppressCount > 0
}

export function runWithLoginPromptSuppressed<T>(fn: () => T): T {
  loginPromptSuppressCount += 1
  try {
    return fn()
  } finally {
    loginPromptSuppressCount -= 1
  }
}

export async function runWithLoginPromptSuppressedAsync<T>(
  fn: () => Promise<T>,
): Promise<T> {
  loginPromptSuppressCount += 1
  try {
    return await fn()
  } finally {
    loginPromptSuppressCount -= 1
  }
}

type VerifiedFetchInit = RequestInit & {
  _signRetry?: boolean
  _automationReported?: boolean
  _turnstileRetry?: boolean
}

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, '')
}

function createUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16)
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8
    return value.toString(16)
  })
}

function getCookieDomainSegment() {
  if (typeof window === 'undefined') return ''
  const host = window.location.hostname
  if (host === 'lip-sync.ai' || host.endsWith('.lip-sync.ai')) {
    return '; Domain=.lip-sync.ai'
  }
  return ''
}

function writeIdentityCookie(name: string, value: string) {
  if (typeof document === 'undefined' || !value) return
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${ONE_YEAR_SECONDS}; Path=/; SameSite=Lax${getCookieDomainSegment()}${secure}`
}

function writeUuidCookie(uuid: string) {
  writeIdentityCookie(UUID_COOKIE_NAME, uuid)
}

function getOrCreateGuestId() {
  const userStore = useUserStore()
  let id = userStore.uuid || localStorage.getItem(UUID_STORAGE_KEY) || ''

  if (!id) {
    id = createUuid()
    localStorage.setItem(UUID_STORAGE_KEY, id)
  }

  if (userStore.uuid !== id) {
    userStore.setUuid(id)
  }

  return id
}

/** 访客/设备标识，经请求头 wapuId 传给后端（不写 cookie） */
export function getRequestWapuId() {
  if (typeof window === 'undefined') return ''
  return getOrCreateGuestId()
}

export function ensureGuestIdentity() {
  if (typeof window === 'undefined') return ''

  const uuid = getOrCreateGuestId()
  writeUuidCookie(uuid)

  return uuid
}

function getStoredToken() {
  if (typeof localStorage === 'undefined') return ''
  return localStorage.getItem('token') || ''
}

export function getRequestIdentityId() {
  const userStore = useUserStore()
  if (userStore.uuid) return userStore.uuid
  return ensureGuestIdentity()
}

export function getRequestAuthorization() {
  const userStore = useUserStore()
  const token = userStore.token || getStoredToken()
  if (token) return token
  return ensureGuestIdentity()
}

export function getRequestVerifyApiBase() {
  const config = useRuntimeConfig()
  const base =
    config.public.baseApiUrl ||
    config.public.baseUrl ||
    config.public.apiBase ||
    ''
  return trimTrailingSlash(String(base || ''))
}

export function resolveApiUrl(path: string) {
  if (/^https?:\/\//i.test(path)) return path

  const base = getRequestVerifyApiBase()
  if (/^https?:\/\//i.test(base)) {
    return `${base}${path.startsWith('/') ? path : `/${path}`}`
  }
  return path.startsWith('/') ? path : `/${path}`
}

export function resolveRequestPath(url = '', baseURL = '') {
  try {
    const fallbackBase =
      baseURL ||
      getRequestVerifyApiBase() ||
      (typeof window !== 'undefined' ? window.location.origin : 'https://lip-sync.ai')
    return new URL(url || '/', fallbackBase.endsWith('/') ? fallbackBase : `${fallbackBase}/`).pathname
  } catch {
    const [path] = String(url || '').split('?')
    return path || '/'
  }
}

export function isSigningBootstrapPath(path: string) {
  return path === SIGNING_BOOTSTRAP_PATH
}

export function shouldSignRequest(path: string) {
  return SIGNING_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))
}

export function serializeRequestBody(data: unknown) {
  if (data == null) {
    return { body: '', normalizedData: data, shouldReplaceData: false }
  }
  if (typeof data === 'string') {
    return { body: data, normalizedData: data, shouldReplaceData: false }
  }
  if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) {
    return { body: data.toString(), normalizedData: data, shouldReplaceData: false }
  }
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return { body: '', normalizedData: data, shouldReplaceData: false }
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return { body: '', normalizedData: data, shouldReplaceData: false }
  }
  if (data instanceof ArrayBuffer) {
    return { body: '', normalizedData: data, shouldReplaceData: false }
  }

  const body = JSON.stringify(data)
  return { body, normalizedData: body, shouldReplaceData: true }
}

function serializeFetchBody(data: unknown) {
  if (data == null) {
    return { body: '', normalizedBody: data }
  }
  if (typeof data === 'string') {
    return { body: data, normalizedBody: data }
  }
  if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) {
    return { body: data.toString(), normalizedBody: data }
  }
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return { body: '', normalizedBody: data }
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return { body: '', normalizedBody: data }
  }
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
    return { body: '', normalizedBody: data }
  }
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(data)) {
    return { body: '', normalizedBody: data }
  }
  if (typeof ReadableStream !== 'undefined' && data instanceof ReadableStream) {
    return { body: '', normalizedBody: data }
  }

  const body = JSON.stringify(data)
  return { body, normalizedBody: body }
}

export async function applySecretResponseHeaders(headers: unknown) {
  if (!import.meta.client || !headers) return
  try {
    const secretManager = useSecretManager()
    await secretManager.handleResponseHeaders(headers as Headers)
  } catch (error) {
    console.warn('[request-verification] failed to update signing secret', error)
  }
}

export function promptLoginForCode(payload: unknown) {
  const code = Number((payload as { code?: unknown } | null)?.code)
  if (code !== 100001 || !import.meta.client || isLoginPromptSuppressed()) return false

  const userStore = useUserStore()
  userStore.setLoginModalShow(true)
  return true
}

function commonHeaders(contentType?: string) {
  const authorization = getRequestAuthorization()
  const identityId = getRequestIdentityId()
  const wapuId = getRequestWapuId()
  return {
    ...(contentType ? { 'Content-Type': contentType } : {}),
    ...(authorization ? { authorization } : {}),
    ...(identityId ? { 'X-Identity-Id': identityId } : {}),
    ...(wapuId ? { [WAPU_ID_HEADER]: wapuId } : {}),
  }
}

async function readJsonIfPossible(response: Response) {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) return null

  try {
    return await response.json()
  } catch {
    return null
  }
}

async function fetchVerificationPath(path: string, init: RequestInit = {}) {
  const response = await fetch(resolveApiUrl(path), {
    ...init,
    credentials: 'include',
    headers: new Headers(init.headers),
  })

  await applySecretResponseHeaders(response.headers)
  const payload = await readJsonIfPossible(response)
  promptLoginForCode(payload)

  return payload
}

export async function bootstrapSigningSecret({ force = false } = {}) {
  if (!import.meta.client) return null
  if (isLocalPreviewRuntime()) return null

  const secretManager = useSecretManager()
  if (!force && secretManager.currentSecret.value) return null
  if (bootstrapPromise) return bootstrapPromise

  bootstrapPromise = (async () => {
    const identityId = getRequestIdentityId()
    const authHeaders = await secretManager.getAuthHeaders(
      'GET',
      SIGNING_BOOTSTRAP_PATH,
      '',
      identityId,
    )

    return fetchVerificationPath(SIGNING_BOOTSTRAP_PATH, {
      method: 'GET',
      headers: {
        ...commonHeaders(),
        ...authHeaders,
      },
    })
  })().finally(() => {
    bootstrapPromise = null
  })

  return bootstrapPromise
}

export async function getSigningHeadersForRequest(
  method: string,
  path: string,
  body = '',
  identityId = '',
) {
  if (!import.meta.client || !shouldSignRequest(path)) return {}
  if (isLocalPreviewRuntime()) return {}

  const secretManager = useSecretManager()
  if (!secretManager.currentSecret.value && !isSigningBootstrapPath(path)) {
    await bootstrapSigningSecret()
  }
  if (!secretManager.currentSecret.value && !isSigningBootstrapPath(path)) {
    return {}
  }

  return secretManager.getAuthHeaders(method, path, body, identityId)
}

function setFetchHeader(headers: Headers, key: string, value?: unknown) {
  if (value == null || value === '') return
  headers.set(key, String(value))
}

export async function verifiedFetch(input: string, init: VerifiedFetchInit = {}) {
  const path = resolveRequestPath(input, getRequestVerifyApiBase())
  const method = (init.method || 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  const authorization = getRequestAuthorization()
  const identityId = getRequestIdentityId()
  const wapuId = getRequestWapuId()
  const { body, normalizedBody } = serializeFetchBody(init.body)

  setFetchHeader(headers, 'authorization', authorization)
  setFetchHeader(headers, WAPU_ID_HEADER, wapuId)
  if (identityId && path.startsWith('/api/aigc/')) {
    setFetchHeader(headers, 'X-Identity-Id', identityId)
  }

  if (import.meta.client) {
    const { useTurnstile, shouldRequireTurnstilePath } = await import(
      '~/composables/useTurnstile.js'
    )
    const turnstile = useTurnstile()
    if (shouldRequireTurnstilePath(path)) {
      const ok = await turnstile.ensureTurnstileToken(path)
      if (!ok) {
        throw new Error('Turnstile verification cancelled')
      }
      setFetchHeader(headers, turnstile.getTurnstileHeaderName(), turnstile.getTurnstileToken())
    }
  }

  if (!init._automationReported) {
    await reportAutomationBeforeCreation(path)
  }

  if (shouldSignRequest(path)) {
    const signingHeaders = await getSigningHeadersForRequest(
      method,
      path,
      body,
      identityId,
    )
    Object.entries(signingHeaders).forEach(([key, value]) => {
      setFetchHeader(headers, key, value)
    })
  }

  const response = await fetch(resolveApiUrl(input), {
    ...init,
    body: normalizedBody as BodyInit | null | undefined,
    credentials: init.credentials || 'include',
    headers,
  })

  await applySecretResponseHeaders(response.headers)
  const payload = await readJsonIfPossible(response.clone())

  if (Number((payload as { code?: unknown } | null)?.code) === 100060 && !init._signRetry) {
    return verifiedFetch(input, {
      ...init,
      body: normalizedBody as BodyInit | null | undefined,
      _signRetry: true,
      _automationReported: true,
    })
  }

  if (
    Number((payload as { code?: unknown } | null)?.code) === 100094 &&
    !init._turnstileRetry &&
    import.meta.client
  ) {
    const { useTurnstile } = await import('~/composables/useTurnstile.js')
    const turnstile = useTurnstile()
    turnstile.clearVerification()
    const ok = await turnstile.ensureTurnstileToken(path, { forceFresh: true })
    if (ok) {
      return verifiedFetch(input, {
        ...init,
        body: normalizedBody as BodyInit | null | undefined,
        _turnstileRetry: true,
        _automationReported: true,
      })
    }
  }

  promptLoginForCode(payload)
  return response
}

async function markFooterGuest() {
  await fetchVerificationPath(FOOTER_MARK_PATH, {
    method: 'GET',
  })
}

export async function postAutomationReport(signals?: string[]) {
  const body = JSON.stringify({ signals: signals ?? (await detectAutomation()) })
  const identityId = getRequestIdentityId()
  const signingHeaders = await getSigningHeadersForRequest(
    'POST',
    AUTOMATION_REPORT_PATH,
    body,
    identityId,
  )

  return fetchVerificationPath(AUTOMATION_REPORT_PATH, {
    method: 'POST',
    body,
    headers: {
      ...commonHeaders('application/json'),
      ...signingHeaders,
    },
  })
}

export async function reportAutomationBeforeCreation(path: string) {
  if (
    !import.meta.client ||
    isLocalPreviewRuntime() ||
    (path !== LIP_SYNC_CREATE_TASK_PATH &&
      path !== TALKING_PHOTO_CREATE_TASK_PATH &&
      path !== AI_AVATAR_CREATE_TASK_PATH &&
      path !== AVATAR_DIALOGUE_CREATE_TASK_PATH)
  )
    return

  await initializeRequestVerification()

  const signals = await detectAutomation()
  if (signals.length === 0) return

  await postAutomationReport(signals)
}

export async function initializeRequestVerification() {
  if (!import.meta.client) return
  if (isLocalPreviewRuntime()) return
  if (initPromise) return initPromise

  initPromise = runWithLoginPromptSuppressedAsync(async () => {
    ensureGuestIdentity()
    const steps = [
      () => markFooterGuest(),
      () => bootstrapSigningSecret({ force: true }),
      async () => postAutomationReport(await detectAutomation()),
    ]

    for (const step of steps) {
      try {
        await step()
      } catch (error) {
        console.warn('[request-verification] initialization step failed', error)
      }
    }
  })

  return initPromise
}
